import React, { useState } from 'react';
import { CONTENTFUL_CONFIG } from '../config/contentfulConfig';

const UserSearch = () => {
    const [passportNo, setPassportNo] = useState('');
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        // Create date object (handles YYYY-MM-DD correctly)
        const date = new Date(dateString);
        // Format to dd-mm-yyyy using en-GB and replacing slashes
        return date.toLocaleDateString('en-GB').replace(/\//g, '-');
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setUserData(null);

        try {
            const query = `?access_token=${CONTENTFUL_CONFIG.ACCESS_TOKEN}&content_type=${CONTENTFUL_CONFIG.CONTENT_TYPE_ID}&fields.passportNo=${passportNo}`;
            const url = `https://cdn.contentful.com/spaces/${CONTENTFUL_CONFIG.SPACE_ID}/environments/master/entries${query}`;

            const response = await fetch(url);
            const data = await response.json();

            if (data.items && data.items.length > 0) {
                // Determine image URL if it exists
                let imageUrl = null;
                const userEntry = data.items[0];
                
                // If there's an image asset linked
                if (userEntry.fields.image && data.includes && data.includes.Asset) {
                    const assetId = userEntry.fields.image.sys.id;
                    const asset = data.includes.Asset.find(a => a.sys.id === assetId);
                    if (asset && asset.fields && asset.fields.file) {
                        imageUrl = asset.fields.file.url;
                    }
                }

                setUserData({ ...userEntry.fields, imageUrl });
            } else {
                setError('No user found with this Passport Number.');
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('An error occurred while fetching details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // STYLES OBJECT (Premium Gold/Blue Theme)
    const styles = {
        sectionHeader: {
            color: '#020e2a',
            fontSize: '1.5rem',
            fontWeight: '700',
            marginBottom: '10px',
            textTransform: 'uppercase',
            letterSpacing: '1px'
        },
        searchCard: {
            background: '#fff',
            padding: '40px',
            borderRadius: '10px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            marginBottom: '40px'
        },
        input: {
            height: '50px',
            border: '2px solid #eee',
            borderRadius: '5px',
            padding: '0 20px',
            fontSize: '16px',
            width: '100%',
            transition: 'border-color 0.3s'
        },
        submitBtn: {
            height: '50px',
            background: '#fab909',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            padding: '0 30px',
            fontSize: '16px',
            fontWeight: '600',
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'background 0.3s',
            width: '100%'
        },
        resultCard: {
            background: '#fff',
            borderRadius: '10px',
            boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            borderTop: '5px solid #fab909'
        },
        resultHeader: {
            background: '#020e2a',
            color: '#fff',
            padding: '20px 30px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        resultBody: {
            padding: '40px'
        },
        label: {
            color: '#888',
            fontSize: '0.85rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '5px',
            fontWeight: '600'
        },
        value: {
            color: '#020e2a',
            fontSize: '1.1rem',
            fontWeight: '600',
            marginBottom: '20px',
            borderBottom: '1px solid #f0f0f0',
            paddingBottom: '10px'
        },
        photoFrame: {
            padding: '10px',
            border: '1px solid #fab909',
            borderRadius: '5px',
            display: 'inline-block'
        }
    };

    return (
        <section className="contact-area pb-100 pt-100" style={{ backgroundColor: '#f4f6f9' }}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        
                        {/* HEADER */}
                        <div className="text-center mb-50">
                            <h2 style={styles.sectionHeader}>Personnel Verification System</h2>
                            <p className="text-muted">Secure Internal Database Search</p>
                        </div>

                        {/* SEARCH BOX */}
                        <div style={styles.searchCard}>
                            <form onSubmit={handleSearch}>
                                <div className="row align-items-end">
                                    <div className="col-lg-9 col-md-8">
                                        <label style={{ fontWeight: '600', marginBottom: '10px', display: 'block' }}>Search by Passport Number</label>
                                        <input 
                                            type="text" 
                                            placeholder="Enter Passport Number (e.g., A1234567)" 
                                            value={passportNo}
                                            onChange={(e) => setPassportNo(e.target.value)}
                                            required
                                            style={styles.input}
                                        />
                                    </div>
                                    <div className="col-lg-3 col-md-4 mt-3 mt-md-0">
                                        <button type="submit" disabled={loading} style={styles.submitBtn}>
                                            {loading ? <i className="fa fa-spinner fa-spin"></i> : 'Verify ID'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* STATUS MESSAGES */}
                        {error && (
                            <div className="alert alert-danger text-center" role="alert">
                                <i className="fa fa-exclamation-circle mr-2"></i> {error}
                            </div>
                        )}

                        {/* RESULT CARD */}
                        {userData && (
                            <div className="fade-in" style={styles.resultCard}>
                                <div style={styles.resultHeader}>
                                    <h3 style={{ margin: 0, color: '#fff', fontSize: '1.3rem' }}>Personnel Record</h3>
                                    <span style={{ background: 'rgba(255,255,255,0.2)', padding: '5px 15px', borderRadius: '20px', fontSize: '0.9rem' }}>
                                        <i className="fa fa-check-circle"></i> Verified
                                    </span>
                                </div>
                                <div style={styles.resultBody}>
                                    <div className="row">
                                        <div className="col-lg-4 text-center mb-4 mb-lg-0">
                                            <div style={styles.photoFrame}>
                                                {userData.imageUrl ? (
                                                    <img 
                                                        src={userData.imageUrl} 
                                                        alt="Personnel" 
                                                        className="img-fluid" 
                                                        style={{ width: '100%', maxHeight: '250px', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <div style={{ width: '200px', height: '220px', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', color: '#aaa', flexDirection: 'column' }}>
                                                        <i className="fa fa-user fa-3x mb-3"></i>
                                                        <span>No Photo</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="mt-3">
                                                <h4 style={{ color: '#fab909', fontWeight: 'bold' }}>{userData.firstName} {userData.lastName}</h4>
                                                <p className="text-muted">{userData.nationality || 'Unknown Nationality'}</p>
                                            </div>
                                        </div>
                                        
                                        <div className="col-lg-8">
                                            <div className="row">
                                                <div className="col-md-6"><div style={styles.label}>Iron Wall ID</div><div style={styles.value}>{userData.idNumber || '-'}</div></div>
                                                <div className="col-md-6"><div style={styles.label}>Passport No</div><div style={styles.value}>{userData.passportNo || '-'}</div></div>
                                                
                                                <div className="col-md-6"><div style={styles.label}>Date of Birth</div><div style={styles.value}>{formatDate(userData.dateOfBirth)}</div></div>
                                                <div className="col-md-6"><div style={styles.label}>Training Program</div><div style={styles.value}>{userData.address || '-'}</div></div>
                                                
                                                <div className="col-md-4"><div style={styles.label}>Visa No</div><div style={styles.value}>{userData.visaNumber || '-'}</div></div>
                                                <div className="col-md-4"><div style={styles.label}>Issued</div><div style={styles.value}>{formatDate(userData.visaIssueDate)}</div></div>
                                                <div className="col-md-4"><div style={styles.label}>Expires</div><div style={styles.value}>{formatDate(userData.visaExpiry)}</div></div>
                                                
                                                <div className="col-12"><div style={styles.label}>Accommodation</div><div style={{...styles.value, borderBottom: 'none'}}>{userData.accommodationAddress || '-'}</div></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                    </div>
                </div>
            </div>
        </section>
    );
};

export default UserSearch;
